<template>
  <div v-if="providersWithUpdatedStatus && providersWithUpdatedStatus.length > 0">
    <div class="provider-cards-container">
      <ProviderCard
        v-for="provider in providersWithUpdatedStatus"
        :key="provider.provider"
        :data-testid="`oc-self-service-provider-${provider.provider}`"
        :title="provider.provider_name"
        :image-src="hostname + provider.provider_logo"
        :selected="selectedCard === provider.id"
        :provider="provider"
        :clickable="true"
        @select="handleCardSelect(provider)" />
    </div>
    <div v-if="loadingProvider" class="settings-panel">
      <div class="form-container">
        <div class="placeholder-glow">
          <div class="placeholder col-12 mb-3"></div>
          <div class="placeholder col-10 mb-3"></div>
          <div class="placeholder col-8 mb-3"></div>
          <div class="placeholder col-12 mb-3"></div>
          <div class="placeholder col-9 mb-3"></div>
          <div class="placeholder col-11 mb-3"></div>
          <div class="placeholder col-7 mb-3"></div>
        </div>
      </div>
      <div class="preview-panel">
        <div class="placeholder-glow" style="height: 100%; padding: 20px">
          <div class="placeholder col-12 mb-3" style="height: 200px"></div>
          <div class="placeholder col-10 mb-3"></div>
          <div class="placeholder col-8 mb-3"></div>
          <div class="placeholder col-12 mb-3"></div>
          <div class="placeholder col-9 mb-3"></div>
        </div>
      </div>
    </div>
    <div v-else class="settings-panel">
      <div class="form-container">
        <component
          v-if="selectedForm && fieldGroups?.length > 0"
          :is="selectedForm.component"
          :provider-id="selectedCard"
          :fieldGroups="fieldGroups"
          :object="object"
          v-bind="selectedForm.needsOrder ? { order: props.order } : {}"
          @update:object="updateObject" />
      </div>
      <div class="preview-panel">
        <div style="height: 100%; display: flex; width: 100%; flex-direction: column">
          <PreviewCampaignPanel
            :provider-id="selectedCard"
            :provider-name="selectedProvider"
            :order-id="orderId"
            :object="object"
            :version="parseInt(object?.id ?? '0')" />
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="text-muted text-center tw:mt-6">{{ $t('campaigns.no_campaigns') }}</div>
    <div v-if="props.isSelfService" class="text-muted text-center">{{ $t('campaigns.contact_support') }}</div>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, watch, computed, type Component } from 'vue';
import ProviderCard from '@/components/orders/provider_cards/ProviderCard.vue';
import LinkedInForm from './provider_forms/LinkedInForm.vue';
import { getAvailableFields, getCampaign } from '@/api/campaign';
import { hostname } from '@/api/client';
import type { CampaignProvider, FieldGroup } from '@/types/types';
import FacebookForm from './provider_forms/FacebookForm.vue';
import ManualForm from './provider_forms/ManualForm.vue';
import { useToast } from 'vue-toastification';
import type { Order } from '@/types/types';
import GoogleAdsDisplayForm from './provider_forms/GoogleAdsDisplayForm.vue';
import GoogleAdsForm from './provider_forms/GoogleAdsForm.vue';
import AdformForm from './provider_forms/AdformForm.vue';
import TikTokForm from './provider_forms/TikTokForm.vue';
import PreviewCampaignPanel from '@/components/campaign/PreviewCampaignPanel.vue';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';
import { debounce } from '@prospective/lithium';

const props = defineProps<{ orderId: string; order: Order; isSelfService?: boolean }>();

const emit = defineEmits<{
  (e: 'update:allowNext', valid: boolean): void;
}>();

const selectedCard = ref<string | null>(); // holds the ID of the selected provider card
const selectedProvider = ref<string>('');

/**
 * Which self-service form renders which provider.
 *
 * A map rather than a branch per provider: they all take the same four props, so a new provider is
 * an entry here. `needsOrder` marks the one form that wants more than those four.
 */
const providerForms: Record<string, { component: Component; needsOrder?: boolean }> = {
  linkedin: { component: LinkedInForm },
  facebook: { component: FacebookForm },
  googleads_display_ad: { component: GoogleAdsDisplayForm, needsOrder: true },
  googleads_display_ad_demand_gen: { component: GoogleAdsDisplayForm, needsOrder: true },
  googleads_search_ad: { component: GoogleAdsForm },
  adform: { component: AdformForm },
  manual: { component: ManualForm },
  tik_tok: { component: TikTokForm },
};

const selectedForm = computed(() => providerForms[selectedProvider.value]);
const fieldGroups = ref<FieldGroup[]>([]);
const toast = useToast();
const object = ref<CampaignProvider>();
const providerStore = useProviderStore();
const { providersWithUpdatedStatus } = storeToRefs(providerStore);
const loadingProvider = ref(false);

const handleCardSelect = debounce((provider: CampaignProvider) => {
  if (selectedCard.value === provider.id) {
    return; // already selected
  }
  fieldGroups.value = [];
  object.value = undefined;
  selectedCard.value = provider.id;
  selectedProvider.value = provider.provider;
}, 150);

const updateObject = (obj: any) => {
  object.value = obj;
  // update the provider store's object for this provider
  providerStore.updateProviderObject(selectedCard.value ?? '', obj);
};

const allProvidersValid = computed(() => {
  if (!providersWithUpdatedStatus.value || providersWithUpdatedStatus.value.length === 0) return false;
  return providersWithUpdatedStatus.value.every(providerStore.isProviderReady);
});

watch(
  allProvidersValid,
  (valid) => {
    if (props.isSelfService) emit('update:allowNext', valid);
  },
  { immediate: true }
);

watch(
  providersWithUpdatedStatus,
  () => {
    if (props.isSelfService) emit('update:allowNext', allProvidersValid.value);
  },
  { deep: true }
);

const hasNextProvider = computed(() => {
  if (!providersWithUpdatedStatus.value || providersWithUpdatedStatus.value.length === 0 || !selectedCard.value)
    return false;
  const currentIndex = providersWithUpdatedStatus.value.findIndex((p) => p.id === selectedCard.value);
  return currentIndex < providersWithUpdatedStatus.value.length - 1;
});

const currentProviderValid = computed(() => {
  if (!selectedCard.value) return false;
  const current = providersWithUpdatedStatus.value.find((p) => p.id === selectedCard.value);
  return !!current && providerStore.isProviderReady(current);
});

const triggerNextProvider = () => {
  const currentIndex = providersWithUpdatedStatus.value.findIndex((p) => p.id === selectedCard.value);
  const nextIndex = currentIndex + 1;
  if (nextIndex < providersWithUpdatedStatus.value.length) {
    handleCardSelect(providersWithUpdatedStatus.value[nextIndex]);
    return true;
  }
  return false;
};

defineExpose({
  triggerNextProvider,
  // Expose as a plain getter so callers don't need to unwrap the computed ref
  get hasNextProvider() {
    return hasNextProvider.value;
  },
  get currentProviderValid() {
    return currentProviderValid.value;
  },
});

onMounted(async () => {
  try {
    await providerStore.getProviders(props.orderId);
    if (providersWithUpdatedStatus.value && providersWithUpdatedStatus.value.length > 0) {
      selectedCard.value = providersWithUpdatedStatus.value[0].id;
      selectedProvider.value = providersWithUpdatedStatus.value[0].provider;
    }
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    toast.error('Error fetching campaigns');
  }
});

watch(selectedCard, async (newValue) => {
  if (newValue) {
    try {
      loadingProvider.value = true;
      // drop the previous campaign's provider values before the new form populates them, so no key
      // the new form does not write itself is carried over into its preview requests
      providerStore.resetProviderValues();
      const campaign = await getCampaign(newValue);
      object.value = campaign.data;

      const groups = await getAvailableFields(newValue);
      fieldGroups.value = groups.groups || [];
      loadingProvider.value = false;
    } catch (error) {
      toast.error('Error fetching provider fields');
      selectedCard.value = null;
      loadingProvider.value = false;
      console.error('Error fetching field groups:', error);
    }
  }
});
</script>

<style scoped lang="scss">
* {
  font-family: var(--font-brand);
}
.provider-cards-container {
  display: grid;
  gap: 24px;
  margin-bottom: 22px;
  grid-template-columns: repeat(auto-fill, 200px);
  overflow-x: auto;
}

.settings-panel {
  display: flex;
  flex-direction: row;
  gap: 24px;
  position: relative;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 536px;
}

.preview-panel {
  border-radius: 10px;
  border-width: 1px;
  border-color: #0000001a;
  border-style: solid;
  width: 536px;
  height: 573px;
  position: sticky;
  overflow-y: auto;
  /* override --settings-preview-top on the parent to clear the sticky header */
  top: var(--settings-preview-top, 50px);
  flex-direction: column;
  justify-content: end;
}

.preview-panel-footer {
  width: 100%;
  padding: 12px 10px;
  border-top: 1px solid #0000001a;
  background: #eaecf0;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  display: flex;
  justify-content: flex-end;
}
</style>
